import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { TablePagination, Box, Button, Autocomplete, TableContainer, Table, Paper, TableHead, TableRow, TableCell, TableBody, Grid, TextField, Accordion, AccordionSummary, AccordionDetails, Typography, MenuItem, TableSortLabel, IconButton } from '@mui/material';
import { Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from '@mui/material';
import { useAuth } from '../context/AuthContext';
import DataQualityDashboard from './DataQualityDashboard';

function fetchRecipes() {
    return fetch(`${import.meta.env.VITE_API_URL}/recipes`).then(response => response.json());
}

function fetchTypes() {
    return fetch(`${import.meta.env.VITE_API_URL}/tipoDeCocina`).then(response => response.json());
}

function fetchRestrictions() {
    return fetch(`${import.meta.env.VITE_API_URL}/restricciones`).then(response => response.json());
}

function fetchIngredients() {
    return fetch(`${import.meta.env.VITE_API_URL}/ingredients`).then(response => response.json());
}

function fetchTypeById(id) {
    return fetch(`${import.meta.env.VITE_API_URL}/tipoDeCocina/${id}`).then(response => response.json());
}
function fetchRecipeCategories() {
    return fetch(`${import.meta.env.VITE_API_URL}/categoriaRecetas`).then(response => response.json());
}

const getRecipeCreationTime = (recipe) => {
    if (recipe.createdAt) {
        const createdAtTime = new Date(recipe.createdAt).getTime();
        if (!Number.isNaN(createdAtTime)) return createdAtTime;
    }

    const objectId = recipe._id || recipe.id;
    if (typeof objectId === 'string' && /^[a-f\d]{24}$/i.test(objectId)) {
        return parseInt(objectId.substring(0, 8), 16) * 1000;
    }

    return null;
};

const formatCreationDate = (recipe) => {
    const creationTime = getRecipeCreationTime(recipe);
    if (!creationTime) return '-';

    return new Intl.DateTimeFormat('es-ES', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
    }).format(new Date(creationTime));
};

function GestionRecetas() {
    const { userRole, authToken } = useAuth();
    console.log("El rol actual es " + userRole);
    const isAdmin = userRole === 'admin';
    const navigate = useNavigate();
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);
    const [searchTerm, setSearchTerm] = useState('');
    const [recipes, setRecipes] = useState([]);
    const [tiposDeCocina, settiposDeCocina] = useState([]);
    const [restrictions, setRestrictions] = useState([]);
    const [selectedType, setSelectedType] = useState('');
    const [selectedRestriction, setSelectedRestriction] = useState('');
    const [ingredients, setIngredients] = useState([]);
    const [selectedIngredients, setSelectedIngredients] = useState([]);
    const [selectedRecipeCategory, setSelectedRecipeCategory] = useState('');
    const [recipeCategories, setRecipeCategories] = useState([]);
    const [selectedTipoPlato, setSelectedTipoPlato] = useState('');
    const [selectedReviewed, setSelectedReviewed] = useState(null);
    const [hasIngredients, setHasIngredients] = useState('');
    const [hasSteps, setHasSteps] = useState('');
    const theme = useTheme();
    const [selectedDifficulty, setSelectedDifficulty] = useState('');
    const [openDialog, setOpenDialog] = useState(false);
    const [recipeToDelete, setRecipeToDelete] = useState(null);
    const [orderDirection, setOrderDirection] = useState('asc');
    const [orderBy, setOrderBy] = useState('');
    const [selectedMomentoDelDia, setSelectedMomentoDelDia] = useState('');
    const [userStats, setUserStats] = useState({});

    useEffect(() => {
        setPage(0);
    }, [searchTerm, selectedType, selectedRestriction, selectedIngredients, selectedDifficulty, hasIngredients, hasSteps]);

    const ingredientOptions = ingredients.map(ingredient => ({
        value: ingredient._id,
        label: ingredient.name
    }));

    useEffect(() => {
        fetchRecipes().then(data => {
            setRecipes(data);
        });
        fetchTypes().then(data => settiposDeCocina(data));
        fetchRestrictions().then(data => setRestrictions(data));
        fetchIngredients().then(data => setIngredients(data));
        fetchRecipeCategories().then(data => setRecipeCategories(data));
        if (authToken) {
            fetch(`${import.meta.env.VITE_API_URL}/recipes/user-stats`, {
                headers: { 'Authorization': `Bearer ${authToken}` }
            })
                .then(res => res.json())
                .then(data => setUserStats(data))
                .catch(err => console.error("Error fetching user stats:", err));
        }
    }, [authToken]);

    const handleRequestSort = (property) => {
        const isAsc = orderBy === property && orderDirection === 'asc';
        setOrderDirection(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const sortRecipes = (array) => {
        return array.sort((a, b) => {
            if (orderBy === '') return 0;
            let valA, valB;
            if (orderBy === 'usageCount') {
                valA = userStats[a._id] || 0;
                valB = userStats[b._id] || 0;
            } else if (orderBy === 'createdAt') {
                valA = getRecipeCreationTime(a) || 0;
                valB = getRecipeCreationTime(b) || 0;
            } else {
                valA = a[orderBy];
                valB = b[orderBy];
            }
            if (valA < valB) {
                return orderDirection === 'asc' ? -1 : 1;
            }
            if (valA > valB) {
                return orderDirection === 'asc' ? 1 : -1;
            }
            return 0;
        });
    };

    const normalize = (s) => (s || '').toString().normalize('NFD').replace(/\p{Diacritic}/gu, '').toLowerCase();
    const filteredRecipes = sortRecipes(recipes.filter(recipe => {
        const matchesType = selectedType === '' || (Array.isArray(recipe.tipoDeCocina) && recipe.tipoDeCocina.some(r => r === selectedType));
        const matchesRestriction = selectedRestriction === '' || (recipe.restricciones && recipe.restricciones.some(r => r === selectedRestriction));
        const matchesDifficulty = selectedDifficulty === '' || recipe.difficulty === selectedDifficulty;
        const matchesSearchTerm = normalize(recipe.name).includes(normalize(searchTerm));
        // Filtro por ingredientes: combinación AND sobre ingredientes usados en la receta,
        // soportando tanto `ingredients` legacy como `components` con refModel 'Ingredient'.
        const usedIngredientIds = new Set([
            ...(Array.isArray(recipe.ingredients) ? recipe.ingredients.map(i => i.ingredientId).filter(Boolean) : []),
            ...(Array.isArray(recipe.components) ? recipe.components
                .filter(c => c && c.refModel === 'Ingredient' && c.refId)
                .map(c => c.refId) : []),
        ]);
        const matchesIngredients = selectedIngredients.length === 0 || selectedIngredients.every(id => usedIngredientIds.has(id));
        const matchesRecipeCategory = selectedRecipeCategory === '' || (recipe.categoriaRecetas && recipe.categoriaRecetas.includes(selectedRecipeCategory));
        const matchesTipoPlato = selectedTipoPlato === '' || (recipe.tipoPlato && recipe.tipoPlato.includes(selectedTipoPlato));
        const matchesReviewed = selectedReviewed === null || recipe.revisado === selectedReviewed;
        const matchesHasIngredients = hasIngredients === '' || (hasIngredients === 'true' && recipe.ingredients && recipe.ingredients.length > 0) || (hasIngredients === 'false' && (!recipe.ingredients || recipe.ingredients.length === 0));
        const matchesHasSteps = hasSteps === '' || (hasSteps === 'true' && recipe.steps && recipe.steps.length > 0) || (hasSteps === 'false' && (!recipe.steps || recipe.steps.length === 0));
        const matchesMomentoDelDia = selectedMomentoDelDia === '' || (
            Array.isArray(recipe.momentoDelDia)
                ? recipe.momentoDelDia.includes(selectedMomentoDelDia)
                : recipe.momentoDelDia === selectedMomentoDelDia
        ); return matchesType && matchesRestriction && matchesDifficulty && matchesSearchTerm &&
            matchesIngredients && matchesRecipeCategory && matchesTipoPlato &&
            matchesReviewed && matchesHasIngredients && matchesHasSteps && matchesMomentoDelDia;
    }));

    const handleOpenDialog = (recipe) => {
        setRecipeToDelete(recipe);
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
    };

    const deleteRecipe = async () => {
        await fetch(`${import.meta.env.VITE_API_URL}/recipes/${recipeToDelete._id}`, {
            method: 'DELETE',
        });
        setRecipes(recipes.filter(recipe => recipe._id !== recipeToDelete._id));
        handleCloseDialog();
    };

    const handleDuplicate = async (recipe) => {
        try {
            // Need authToken? Check useAuth.
            // useAuth provides { userRole, authToken }? Let's check the context usage above.
            // Line 36: const { userRole } = useAuth();
            // We need authToken to use in headers generally, but 'fetch' wrappers at top don't use it?
            // Wait, fetchRecipes (line 12), fetchTypes, etc don't use auth headers?
            // That implies public GET? But DELETE probably needs it.
            // Oh, deleteRecipe (line 140) doesn't use headers?? That might be a separate issue or legacy.
            // However, the new endpoint might require it if we used `req.user` in backend.
            // Yes, backend uses `req.user`. So we definitely need auth header.
            // Let's get authToken from useAuth() hook.
            // I need to update line 36 first to get authToken.
            const response = await fetch(`${import.meta.env.VITE_API_URL}/recipes/${recipe._id}/duplicate`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${authToken}` // We need to destructure this
                }
            });

            if (response.ok) {
                const newRecipe = await response.json();
                navigate(`/editar-receta/${newRecipe._id}`);
            } else {
                console.error("Error duplicando receta");
            }
        } catch (error) {
            console.error("Error duplicando receta:", error);
        }
    };

    const exportRecetasSinFoto = () => {
        const ingredientMap = {};
        ingredients.forEach(ing => { ingredientMap[ing._id] = ing.name; });

        const recetasSinFoto = recipes.filter(r => !r.mainImage);

        const escapeCsv = (value) => {
            const str = String(value || '');
            if (str.includes(',') || str.includes('"') || str.includes('\n')) {
                return '"' + str.replace(/"/g, '""') + '"';
            }
            return str;
        };

        const header = ['Nombre', 'Ingredientes', 'Pasos'];
        const rows = recetasSinFoto.map(recipe => {
            const ingredientsList = [];

            if (Array.isArray(recipe.components) && recipe.components.length > 0) {
                recipe.components.forEach(comp => {
                    if (comp.refModel === 'Ingredient' && comp.refId) {
                        const name = ingredientMap[comp.refId] || 'Desconocido';
                        ingredientsList.push(`${name}: ${comp.quantity || ''} ${comp.unit || ''}`.trim());
                    }
                });
            }

            if (ingredientsList.length === 0 && Array.isArray(recipe.ingredients)) {
                recipe.ingredients.forEach(ing => {
                    const name = ingredientMap[ing.ingredientId] || 'Desconocido';
                    ingredientsList.push(`${name}: ${ing.quantity || ''} ${ing.unit || ''}`.trim());
                });
            }

            const stepsText = Array.isArray(recipe.steps) ? recipe.steps.map((s, i) => `${i + 1}. ${s}`).join(' | ') : '';

            return [
                escapeCsv(recipe.name),
                escapeCsv(ingredientsList.join('; ')),
                escapeCsv(stepsText)
            ].join(',');
        });

        const csvContent = '\uFEFF' + [header.join(','), ...rows].join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'recetas_sin_foto.csv';
        link.click();
        URL.revokeObjectURL(url);
    };

    return (
        <Grid className="modern-admin-page" container spacing={2}>
            <Box display="flex" justifyContent="center" textAlign="center" width="100%" alignItems="center" height="100px" gap={2}>
                <Button component={Link} to="/recetas/crear" variant="contained" sx={{ bgcolor: 'primary.main', color: '#fff' }} size='large'>
                    Nueva Receta
                </Button>
                <Button
                    variant="outlined"
                    startIcon={<FileDownloadIcon />}
                    onClick={exportRecetasSinFoto}
                    size="large"
                >
                    Exportar recetas sin foto
                </Button>
            </Box>
            <Grid item xs={12}>
                <DataQualityDashboard data={recipes} type="recipes" />
            </Grid>
            <Grid item xs={12}>
                <Accordion defaultExpanded>
                    <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        aria-controls="panel1a-content"
                        id="panel1a-header"
                    >
                        <Typography>Filtrar recetas</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6} md={3}>
                                <TextField
                                    fullWidth
                                    label="Buscar recetas..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <TextField
                                    select
                                    label="Tipo de Cocina"
                                    value={selectedType}
                                    onChange={(e) => setSelectedType(e.target.value)}
                                    variant="outlined"
                                    fullWidth
                                >
                                    <MenuItem value=""><em>Ninguno</em></MenuItem>
                                    {tiposDeCocina.map((type) => (
                                        <MenuItem key={type._id} value={type._id}>
                                            {type.name}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <TextField
                                    select
                                    label="Preferencias"
                                    value={selectedRestriction}
                                    onChange={(e) => setSelectedRestriction(e.target.value)}
                                    variant="outlined"
                                    fullWidth
                                >
                                    <MenuItem value=""><em>Ninguno</em></MenuItem>
                                    {restrictions.map((restriction) => (
                                        <MenuItem key={restriction._id} value={restriction._id}>
                                            {restriction.name}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <Autocomplete
                                    multiple
                                    id="tags-outlined"
                                    options={ingredientOptions}
                                    getOptionLabel={(option) => option.label}
                                    filterSelectedOptions
                                    onChange={(event, newValue) => {
                                        setSelectedIngredients(newValue.map((option) => option.value));
                                    }}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            variant="outlined"
                                            label="Selecciona ingredientes"
                                        />
                                    )}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <TextField
                                    select
                                    label="Dificultad"
                                    value={selectedDifficulty}
                                    onChange={(e) => setSelectedDifficulty(e.target.value)}
                                    variant="outlined"
                                    fullWidth
                                >
                                    <MenuItem value=""><em>Ninguno</em></MenuItem>
                                    <MenuItem value="Baja">Fácil</MenuItem>
                                    <MenuItem value="Media">Media</MenuItem>
                                    <MenuItem value="Alta">Difícil</MenuItem>
                                </TextField>
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <TextField
                                    select
                                    label="Categorías de recetas"
                                    value={selectedRecipeCategory}
                                    onChange={(e) => setSelectedRecipeCategory(e.target.value)}
                                    variant="outlined"
                                    fullWidth
                                >
                                    <MenuItem value=""><em>Ninguno</em></MenuItem>
                                    {recipeCategories.map((category) => (
                                        <MenuItem key={category._id} value={category._id}>
                                            {category.nombre}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <TextField
                                    select
                                    label="Tipo de plato"
                                    value={selectedTipoPlato}
                                    onChange={(e) => setSelectedTipoPlato(e.target.value)}
                                    variant="outlined"
                                    fullWidth
                                >
                                    <MenuItem value=""><em>Ninguno</em></MenuItem>
                                    <MenuItem value="Desayuno">Desayuno</MenuItem>
                                    <MenuItem value="Snack">Snack</MenuItem>
                                    <MenuItem value="Entrante">Entrante</MenuItem>
                                    <MenuItem value="Principal">Principal</MenuItem>
                                    <MenuItem value="Postre">Postre</MenuItem>
                                    <MenuItem value="Guarnición">Guarnición</MenuItem>
                                    <MenuItem value="Bebidas">Bebidas</MenuItem>
                                </TextField>
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <TextField
                                    select
                                    label="Revisado"
                                    value={selectedReviewed === null ? '' : String(selectedReviewed)}
                                    onChange={(e) => {
                                        setSelectedReviewed(e.target.value === '' ? null : e.target.value === 'true');
                                        console.log("Filtro aplicado: ", e.target.value === '' ? null : e.target.value === 'true');
                                    }}
                                    variant="outlined"
                                    fullWidth
                                >
                                    <MenuItem value=""><em>Ninguno</em></MenuItem>
                                    <MenuItem value="true">Si</MenuItem>
                                    <MenuItem value="false">No</MenuItem>
                                </TextField>
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <TextField
                                    select
                                    label="Tiene ingredientes"
                                    value={hasIngredients}
                                    onChange={(e) => setHasIngredients(e.target.value)}
                                    variant="outlined"
                                    fullWidth
                                >
                                    <MenuItem value=""><em>Ninguno</em></MenuItem>
                                    <MenuItem value="true">Sí</MenuItem>
                                    <MenuItem value="false">No</MenuItem>
                                </TextField>
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <TextField
                                    select
                                    label="Tiene pasos"
                                    value={hasSteps}
                                    onChange={(e) => setHasSteps(e.target.value)}
                                    variant="outlined"
                                    fullWidth
                                >
                                    <MenuItem value=""><em>Ninguno</em></MenuItem>
                                    <MenuItem value="true">Sí</MenuItem>
                                    <MenuItem value="false">No</MenuItem>
                                </TextField>
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <TextField
                                    select
                                    label="Momento del día"
                                    value={selectedMomentoDelDia}
                                    onChange={(e) => setSelectedMomentoDelDia(e.target.value)}
                                    variant="outlined"
                                    fullWidth
                                >
                                    <MenuItem value=""><em>Ninguno</em></MenuItem>
                                    <MenuItem value="Desayuno">Desayuno</MenuItem>
                                    <MenuItem value="Comida">Comida</MenuItem>
                                    <MenuItem value="Cena">Cena</MenuItem>
                                    <MenuItem value="Snack">Snack</MenuItem>
                                </TextField>
                            </Grid>
                        </Grid>
                    </AccordionDetails>
                </Accordion>
            </Grid>
            <Grid item xs={12}>
                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell style={{ width: '120px' }}></TableCell>
                                <TableCell>Nombre</TableCell>
                                <TableCell>
                                    <TableSortLabel
                                        active={orderBy === 'tipoDeCocina'}
                                        direction={orderBy === 'tipoDeCocina' ? orderDirection : 'asc'}
                                        onClick={() => handleRequestSort('tipoDeCocina')}
                                    >
                                        Tipo de Cocina
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell>
                                    <TableSortLabel
                                        active={orderBy === 'restricciones'}
                                        direction={orderBy === 'restricciones' ? orderDirection : 'asc'}
                                        onClick={() => handleRequestSort('restricciones')}
                                    >
                                        Preferencias
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell>
                                    <TableSortLabel
                                        active={orderBy === 'difficulty'}
                                        direction={orderBy === 'difficulty' ? orderDirection : 'asc'}
                                        onClick={() => handleRequestSort('difficulty')}
                                    >
                                        Dificultad
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell>
                                    <TableSortLabel
                                        active={orderBy === 'ingredients'}
                                        direction={orderBy === 'ingredients' ? orderDirection : 'asc'}
                                        onClick={() => handleRequestSort('ingredients')}
                                    >
                                        Tiene ingredientes
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell>
                                    <TableSortLabel
                                        active={orderBy === 'steps'}
                                        direction={orderBy === 'steps' ? orderDirection : 'asc'}
                                        onClick={() => handleRequestSort('steps')}
                                    >
                                        Tiene pasos
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell>
                                    <TableSortLabel
                                        active={orderBy === 'revisado'}
                                        direction={orderBy === 'revisado' ? orderDirection : 'asc'}
                                        onClick={() => handleRequestSort('revisado')}
                                    >
                                        Revisado
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell>
                                    <TableSortLabel
                                        active={orderBy === 'usageCount'}
                                        direction={orderBy === 'usageCount' ? orderDirection : 'asc'}
                                        onClick={() => handleRequestSort('usageCount')}
                                    >
                                        Cocinada
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell>
                                    <TableSortLabel
                                        active={orderBy === 'createdAt'}
                                        direction={orderBy === 'createdAt' ? orderDirection : 'asc'}
                                        onClick={() => handleRequestSort('createdAt')}
                                    >
                                        Fecha creación
                                    </TableSortLabel>
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filteredRecipes
                                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                .map((recipe) => (
                                    <TableRow key={recipe.id}>
                                        <TableCell>
                                            <Box display="flex" gap={1}>
                                                {recipe._id && (
                                                    <IconButton
                                                        component={Link}
                                                        to={`/editar-receta/${recipe._id}`}
                                                        className="btn btn-primary"
                                                        size="small"
                                                        aria-label="Editar receta"
                                                    >
                                                        <EditIcon />
                                                    </IconButton>
                                                )}
                                                <IconButton
                                                    onClick={() => handleDuplicate(recipe)}
                                                    className="btn btn-outline"
                                                    size="small"
                                                    aria-label="Duplicar receta"
                                                    sx={{ color: 'info.main' }}
                                                >
                                                    <ContentCopyIcon />
                                                </IconButton>
                                                <IconButton
                                                    onClick={() => handleOpenDialog(recipe)}
                                                    className="btn btn-outline"
                                                    size="small"
                                                    aria-label="Eliminar receta"
                                                    style={{ color: 'var(--error-500)' }}
                                                >
                                                    <DeleteIcon />
                                                </IconButton>
                                            </Box>
                                        </TableCell>
                                        <TableCell>{recipe.name}</TableCell>
                                        <TableCell>
                                            {Array.isArray(recipe.tipoDeCocina)
                                                ? recipe.tipoDeCocina
                                                    .map((tipoDeCocinaId) => {
                                                        const tipoDeCocina = tiposDeCocina.find((t) => t._id === tipoDeCocinaId);
                                                        return tipoDeCocina ? tipoDeCocina.name : '';
                                                    })
                                                    .join(', ')
                                                : ''}
                                        </TableCell>
                                        <TableCell>
                                            {recipe.restricciones
                                                ? recipe.restricciones
                                                    .map((restrictionId) => {
                                                        const restriction = restrictions.find((r) => r._id === restrictionId);
                                                        return restriction ? restriction.name : '';
                                                    })
                                                    .join(', ')
                                                : ''}
                                        </TableCell>
                                        <TableCell>{recipe.difficulty}</TableCell>
                                        <TableCell>{recipe.ingredients && recipe.ingredients.length > 0 ? 'Si' : 'No'}</TableCell>
                                        <TableCell>{recipe.steps && recipe.steps.length > 0 ? 'Si' : 'No'}</TableCell>
                                        <TableCell>{recipe.revisado ? 'Si' : 'No'}</TableCell>
                                        <TableCell align="center">{userStats[recipe._id] || 0}</TableCell>
                                        <TableCell>{formatCreationDate(recipe)}</TableCell>
                                    </TableRow>
                                ))}
                        </TableBody>
                    </Table>
                    <TablePagination
                        rowsPerPageOptions={[25, 50, 100]}
                        component="div"
                        count={filteredRecipes.length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={(event, newPage) => setPage(newPage)}
                        onRowsPerPageChange={(event) => {
                            setRowsPerPage(parseInt(event.target.value, 10));
                            setPage(0);
                        }}
                    />
                </TableContainer>

                <Dialog
                    open={openDialog}
                    onClose={handleCloseDialog}
                >
                    <DialogTitle>Eliminar Receta</DialogTitle>
                    <DialogContent>
                        <DialogContentText>
                            ¿Estás seguro de que quieres eliminar esta receta?
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleCloseDialog}>Cancelar</Button>
                        <Button onClick={deleteRecipe} color="error">Eliminar</Button>
                    </DialogActions>
                </Dialog>
            </Grid>
        </Grid>
    );
}

export default GestionRecetas;
